import classNames from 'classnames';
import {
  Input,
  FlexContainer,
  Spinner,
  Button,
  Txt,
  ContentBlock,
  View,
} from 'components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { functions } from 'firebaseSDK';
import styles from './InputAddress.module.scss';

/**
 * InputAddress
 *
 * @return {JSX.Element}
 */
const InputAddress = ({
  className,
  value,
  onChange,
  fullWidth,
  name,
  useButton,
  disabled,
  autoFocus,
  reverse,
  ...rest
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [code, setCode] = useState(value?.postcode || '');

  const handleCodeChange = ({ target: { value: localValue } }) => {
    if (value && localValue !== value.postcode) {
      onChange({ target: { name } });
    }
    setAddresses([]);
    setHasSearched(false);
    setCode(localValue);
  };
  const handleHiddenFocus = () => inputRef.current?.focus();
  const handleFocus = () => setIsFocused(true);
  const handleAddressClick = (a) => () => {
    onChange({ target: { name, value: a } });
    setAddresses([]);
  };

  const handleLookup = useCallback((codeToLookup) => {
    if (codeToLookup.length >= 5) {
      setIsLoading(true);
      functions.property
        .lookupAddressFromPostcode({ postcode: codeToLookup })
        .then(({ data: { addresses } }) => {
          setHasSearched(true);
          setAddresses(addresses);
        })
        .catch((e) => console.log('Error', e))
        .finally(() => setIsLoading(false));
    }
  }, []);

  const handleLookupClick = () => {
    handleLookup(code);
    inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (!value && useButton && !isLoading) {
        handleLookup(code);
        e.preventDefault();
      }
    }
  };

  let hiddenValue = '';
  if (typeof value !== 'undefined') {
    try {
      hiddenValue = value ? JSON.stringify(value) : '';
    } catch (e) {
      hiddenValue = '';
    }
  }

  useEffect(() => {
    if (!useButton && code && !value) {
      const timer = setTimeout(() => handleLookup(code), 500);

      return () => clearTimeout(timer);
    }
  }, [code, value, handleLookup, useButton]);

  useEffect(() => {
    if (isFocused) {
      const listener = (e) => {
        const isFocusLost = !(
          !e.target.isConnected || // Check if node is in the document
          e.target === containerRef.current ||
          containerRef.current.contains(e.target)
        );

        if (isFocusLost) {
          setIsFocused(false);
        }
      };

      document.addEventListener('focusin', listener);
      document.addEventListener('click', listener);

      return () => {
        document.removeEventListener('focusin', listener);
        document.removeEventListener('click', listener);
      };
    }
  }, [isFocused]);

  const isAutocompleteShown = isFocused && !isLoading && addresses.length;

  return (
    <FlexContainer
      className={classNames(
        styles.InputAddress,
        fullWidth && styles.InputAddress__fullWidth,
        isAutocompleteShown && styles.InputAddress__showAuto,
        useButton && styles.InputAddress__useButton,
        className,
        `${InputAddress.displayName}`
      )}
      column
      gapHalf
      ref={containerRef}
    >
      <FlexContainer className={styles.InputContainer} gapQuad column>
        <input
          {...rest}
          name={name}
          className={styles.HiddenInput}
          value={hiddenValue}
          onChange={() => {}}
          onFocus={handleHiddenFocus}
          disabled={disabled}
        />
        <Input
          ref={inputRef}
          className={styles.Input}
          fullWidth={fullWidth}
          value={code}
          minLength="5"
          maxLength="9"
          onChange={handleCodeChange}
          title="You must use a valid UK Postcode"
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          reverse={reverse}
        />
        {!useButton && isLoading ? (
          <Spinner className={styles.Spinner} />
        ) : null}
        {useButton && !disabled ? (
          <Button
            className={styles.ButtonLookup}
            disabled={isLoading || !(code.length >= 5)}
            onClick={handleLookupClick}
            success
          >
            {isLoading ? <Spinner /> : <Txt>Search address</Txt>}
          </Button>
        ) : null}
        {isAutocompleteShown ? (
          <ContentBlock className={styles.Autocomplete} column noPadding>
            {addresses.map((a) => (
              <Button
                key={`${a.name}|${a.lat}|${a.long}`}
                square
                transparent
                onClick={handleAddressClick(a)}
              >
                <Txt>{a.name}</Txt>
              </Button>
            ))}
          </ContentBlock>
        ) : null}
      </FlexContainer>
      <FlexContainer>
        {value ? (
          <View>
            <Txt>Selected address: </Txt>
            <Txt x>
              {[value.town, value.line2, value.line1]
                .filter(Boolean)
                .join(', ')}
            </Txt>
          </View>
        ) : hasSearched && !addresses.length ? (
          <Txt error>Nothing found by postcode</Txt>
        ) : (
          <Txt regular>
            Please enter valid UK postcode and search your exact address
          </Txt>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

InputAddress.displayName = 'InputAddress';

export default InputAddress;
